import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import { drawerWidth } from '../../../store/constant';

// JWTContext içinde ekran genişliğine göre body'ye uygulanan manuel zoom'u
// (örn. "77%", "65%") okuyup sayısal değere çevirir. Zoom yoksa 1 döner.
const readBodyZoom = () => {
    const raw = document.body.style.zoom;
    if (!raw) return 1;
    const str = String(raw).trim();
    if (str.endsWith('%')) {
        const n = parseFloat(str.slice(0, -1));
        return isNaN(n) || n === 0 ? 1 : n / 100;
    }
    const n = parseFloat(str);
    return isNaN(n) || n === 0 ? 1 : n;
};

// Component içindeki `window` prop'u global window'u gölgelediği için
// gerçek (tarayıcı) window referansını modül seviyesinde yakalıyoruz.
const rootWindow = typeof window !== 'undefined' ? window : undefined;

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = ({ drawerOpen, drawerToggle, window }) => {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const [bodyZoom, setBodyZoom] = useState(readBodyZoom());

    useEffect(() => {
        const updateZoom = () => setBodyZoom(readBodyZoom());
        updateZoom();
        if (!rootWindow) return;
        // Zoom, JWTContext içinde resize ve doğrudan style atamasıyla değişebilir;
        // hem resize'ı dinleriz hem de body style değişimini gözlemleyiz.
        rootWindow.addEventListener('resize', updateZoom);
        const observer = new MutationObserver(updateZoom);
        observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
        return () => {
            rootWindow.removeEventListener('resize', updateZoom);
            observer.disconnect();
        };
    }, []);

    // Body zoom uygulandığında layout pikseli ile ekrandaki piksel ayrışır.
    // Header (88px/56px) de zoom'lanır; PerfectScrollbar'ın görsel olarak ekranı
    // tam doldurması için layout-piksel yüksekliği: (100vh / zoom) - headerPx
    const headerHeight = matchUpMd ? 88 : 56;
    const scrollHeight = `calc(${100 / bodyZoom}vh - ${headerHeight}px)`;

    const drawer = (
        <>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
                    <LogoSection />
                </Box>
            </Box>
            <BrowserView>
                <PerfectScrollbar
                    component="div"
                    style={{
                        height: scrollHeight,
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        overflowX: 'hidden'
                    }}
                    options={{ suppressScrollX: true }}
                >
                    <MenuList />
                </PerfectScrollbar>
            </BrowserView>
            <MobileView>
                <Box sx={{ px: 2 }}>
                    <MenuList />
                </Box>
            </MobileView>
        </>
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    return (
        <Box component="nav" sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }} aria-label="mailbox folders">
            <Drawer
                container={container}
                variant={matchUpMd ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={drawerToggle}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        background: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderRight: 'none',
                        [theme.breakpoints.up('md')]: {
                            top: '88px'
                        }
                    }
                }}
                ModalProps={{ keepMounted: true }}
                color="inherit"
            >
                {drawer}
            </Drawer>
        </Box>
    );
};

Sidebar.propTypes = {
    drawerOpen: PropTypes.bool,
    drawerToggle: PropTypes.func,
    window: PropTypes.object
};

export default Sidebar;
